// apps/merchant-v3/src/components/banners/SuspendBanner.tsx
//
// Phase 6 Wave 12 Task 42 — Persistent red suspend banner mounted at the
// top of AppShell (above OverdueInvoiceBanner, below ImpersonateBanner).
// Renders ONLY when `useUser().merchantSuspended === true`. Cannot be
// dismissed. Includes a "Liên hệ Support" link
// (mailto:support@printway.work or /support route) carrying the
// data-testid `suspend-banner-contact-link` for E2E.
//
// Distinct from the legacy Wave 1 `components/SuspendBanner.tsx` shim:
// this is the canonical banner used by the Wave 12 BannerStack composition
// and reads the new `merchantSuspended` field added to the User type in
// the same task. The legacy file remains so the Wave 1/2 callers
// (AppShell pre-Task 42 layout) keep compiling — it is removed from the
// AppShell mount point in this task.
//
// Override-reason: the design mockup placed the suspend banner inline
// with the dashboard hero, but requires it to be persistent at
// the top of every page so the merchant can never miss the lockdown
// signal. Mount point is therefore the AppShell top, not the dashboard.
//
// vg-binding: PLAN:task-42
// vg-binding: P6.D-114
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import { AlertTriangle } from 'lucide-react';

import { useUser } from '@/store/user';
import { useTranslation } from '@/lib/i18n';

/** Support contact destination — mailto fallback when the in-app
 *  /support route is feature-flagged off. The Sidebar Support entry is
 *  already gated by `VITE_SUPPORT_ENABLED`; the banner mirrors the
 *  same gate so the link points at the right surface. */
const SUPPORT_HREF: string =
  (import.meta.env as Record<string, string | undefined>).VITE_SUPPORT_ENABLED ===
  'true'
    ? '/tickets'
    : 'mailto:support@printway.work';

export function SuspendBanner(): JSX.Element | null {
  const user = useUser();
  const { t } = useTranslation('common');

  if (!user?.merchantSuspended) return null;

  return (
    <div
      role="alert"
      data-testid="suspend-banner"
      className="sticky top-0 z-30 flex items-center gap-3 border-b border-red-300 bg-red-600 px-4 py-2.5 text-sm text-white shadow-sm"
    >
      <AlertTriangle
        className="h-5 w-5 shrink-0 text-white"
        aria-hidden
      />
      <p className="flex-1 font-medium">
        <span className="font-semibold">
          {t('banners.suspend.title', { defaultValue: 'Account suspended' })}.
        </span>{' '}
        <span className="font-normal text-white/90">
          {t('banners.suspend.message', {
            defaultValue:
              'Your account is suspended. Contact Admin Support to restore.',
          })}
        </span>
        {user.merchantSuspendReason ? (
          <span className="ml-2 italic text-white/80">
            ({user.merchantSuspendReason})
          </span>
        ) : null}
      </p>
      <a
        href={SUPPORT_HREF}
        data-testid="suspend-banner-contact-link"
        className="inline-flex h-8 items-center rounded-md border border-white/40 bg-pw-card/10 px-3 text-xs font-semibold text-white hover:bg-pw-card/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        {t('banners.suspend.contact', { defaultValue: 'Contact Support' })}
      </a>
    </div>
  );
}

export default SuspendBanner;
